import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Typography, Button, Container, IconButton } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LanguagesMenu from "../../../MicrospecDynamicUI/Components/LanguagesMenu";

const NavBar = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const brand = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.nav.brand",
  });
  const getStarted = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.nav.getStarted",
  });

  const navLinks = [
    {
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.nav.about",
      }),
      href: "/about",
    },
    {
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.nav.features",
      }),
      href: "#features",
    },
    {
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.nav.howItWorks",
      }),
      href: "#how-it-works",
    },
    {
      label: localizationProvider.translateByType({
        type: "Localized",
        text: "landing.nav.contact",
      }),
      href: "#contact",
    },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/50 backdrop-blur-3xl shadow-xl shadow-[#8B5CF6]/5"
            : "bg-transparent"
        }`}
        style={
          scrolled
            ? {
                borderBottom: "1px solid rgba(139,92,246,0.08)",
                boxShadow:
                  "0 4px 30px rgba(139,92,246,0.06), 0 1px 0 rgba(192,132,252,0.1)",
              }
            : {}
        }
      >
        <Container maxWidth="xl" dir={dir}>
          <div className="flex items-center justify-between py-4 px-4 md:px-8">
            <a
              href="#home"
              className="flex items-center gap-3 no-underline group"
            >
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7393B3] via-[#8B5CF6] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#8B5CF6]/25 group-hover:shadow-[#8B5CF6]/40 transition-shadow duration-300">
                  <PsychologyIcon sx={{ color: "white", fontSize: 24 }} />
                </div>
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#7393B3]/20 to-[#8B5CF6]/20 blur-sm -z-10" />
              </div>
              <Typography
                variant="h6"
                className={`font-bold tracking-tight transition-colors duration-300 ${scrolled ? "text-slate-800" : "text-[#2D1B4E]"}`}
              >
                {brand}
              </Typography>
            </a>

            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.href}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline ${
                    scrolled
                      ? "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                      : "text-[#5B4A7C] hover:text-[#2D1B4E] hover:bg-[#8B5CF6]/5"
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="contained"
                size="small"
                href="#contact"
                className="hidden md:inline-flex"
                sx={{
                  background:
                    "linear-gradient(135deg, #7393B3 0%, #8B5CF6 100%)",
                  borderRadius: "9999px",
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: "0 4px 15px rgba(139, 92, 246, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5A7A9A 0%, #7C3AED 100%)",
                    boxShadow: "0 8px 25px rgba(139, 92, 246, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {getStarted}
              </Button>

              <LanguagesMenu size="medium" />

              <IconButton
                className="md:hidden"
                onClick={() => setMobileOpen(true)}
                sx={{ color: scrolled ? "#1e293b" : "#2D1B4E" }}
              >
                <MenuRoundedIcon />
              </IconButton>
            </div>
          </div>
        </Container>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: dir === "rtl" ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              exit={{ x: dir === "rtl" ? "-100%" : "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className={`fixed top-0 ${dir === "rtl" ? "left-0" : "right-0"} w-80 h-full bg-white/80 backdrop-blur-3xl z-[70] shadow-2xl shadow-[#8B5CF6]/10 border-l border-[#8B5CF6]/5`}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7393B3] to-[#8B5CF6] flex items-center justify-center">
                      <PsychologyIcon sx={{ color: "white", fontSize: 22 }} />
                    </div>
                    <Typography className="font-bold text-slate-800">
                      {brand}
                    </Typography>
                  </div>
                  <IconButton onClick={() => setMobileOpen(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </div>
                <div className="flex flex-col gap-2">
                  {navLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 rounded-xl text-slate-600 hover:text-slate-800 hover:bg-slate-50 font-medium text-base transition-colors no-underline"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
                <div className="mt-8">
                  <Button
                    variant="contained"
                    fullWidth
                    href="#contact"
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #7393B3 0%, #8B5CF6 100%)",
                      borderRadius: "14px",
                      textTransform: "none",
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {getStarted}
                  </Button>
                </div>

                <div className="mt-4 flex justify-center">
                  <LanguagesMenu size="medium" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
